import { format as formatDate } from "date-fns";
import {
  de,
  el,
  enGB,
  es,
  fi,
  fr,
  it,
  ko,
  nb,
  nl,
  pl,
  sv,
  tr,
} from "date-fns/locale";
import React, { useMemo } from "react";
import styled from "styled-components";
import { CancellationProposalFlightBound } from "../../../types/Cancellation";
import { useTranslationV3 } from "../../../i18nV3/useTranslationV3";
import { Breakpoint } from "../../../enums/Breakpoints";
import { BoundHeavyText, BoundRegularText } from "./Typography";
import { useConfig } from "../../../contexts";
import StopsIndicator from "./StopsIndicator";
import useMedia from "../../../hooks/useMedia";

enum Variant {
  Column = "COLUMN", // Bounds are placed in one column
  Row = "ROW", // Bounds are placed in rows (2 for each column)
}

// TODO: move to some const or maybe hooks like in tix-ui
let locales: { [key: string]: Locale } = {
  de,
  el,
  enGB,
  es,
  fi,
  fr,
  it,
  ko,
  nb,
  nl,
  pl,
  sv,
  tr,
};

interface Props {
  bounds: CancellationProposalFlightBound[];
  className?: string;
  isCancelled?: boolean;
}

const FlightBounds = ({ bounds, className, isCancelled }: Props) => {
  const { t } = useTranslationV3();
  const config = useConfig();
  const isMinTablet = useMedia(Breakpoint.MinTablet);

  const locale = useMemo(
    () => locales[config.defaultLang] || locales.enGB,
    [config.defaultLang]
  );

  const variant = useMemo(() => {
    if (isMinTablet && bounds.length === 2) return Variant.Row;
    return Variant.Column;
  }, [isMinTablet, bounds.length]);

  return (
    <Container className={className} isCancelled={isCancelled} variant={variant}>
      {isCancelled && (
        <CancelledBadge>{t("lp_cancellation_cancelled")}</CancelledBadge>
      )}
      {bounds.map((bound, index) => {
        const departureDate = new Date(bound.departureDateTime);
        const arrivalDate = new Date(bound.arrivalDateTime);
        return (
          <BoundContainer
            key={index}
            isCancelled={isCancelled}
            variant={variant}
          >
            <AirportNames>{`${bound.departure.city} - ${bound.destination.city}`}</AirportNames>
            <FlightData>
              <FlightStop>
                <BoundRegularText>
                  {bound.departure.code}
                </BoundRegularText>
                <BoundHeavyText>
                  {formatDate(new Date(departureDate), "HH:mm", { locale })}
                </BoundHeavyText>
                <BoundRegularText>
                  {formatDate(new Date(departureDate), "eee dd MMM", {
                    locale,
                  })}
                </BoundRegularText>
              </FlightStop>
              <StopsIndicator
                duration={bound.duration}
                numberOfStops={bound.numberOfStops}
                width={118}
              />
              <FlightStop isDestination>
                <BoundRegularText>
                  {bound.destination.code}
                </BoundRegularText>
                <BoundHeavyText>
                  {formatDate(new Date(arrivalDate), "HH:mm", { locale })}
                </BoundHeavyText>
                <BoundRegularText>
                  {formatDate(new Date(arrivalDate), "eee dd MMM", { locale })}
                </BoundRegularText>
              </FlightStop>
            </FlightData>
          </BoundContainer>
        );
      })}
    </Container>
  );
};

const Container = styled.div<{ isCancelled?: boolean; variant: Variant }>`
  background: ${({ theme }) => theme.redesign.defaultBackground};
  box-sizing: border-box;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: ${({ variant }) =>
    variant === Variant.Column ? "column" : "row"};

  ${({ theme, isCancelled }) =>
    isCancelled
      ? `
  border: 1px solid ${theme.redesign.errorColor};
  `
      : ``}

  @media ${Breakpoint.MinTablet} {
    padding: ${({ variant }) =>
      variant === Variant.Column ? "10px 0" : "10px 30px"};
  }
`;

const CancelledBadge = styled.div`
  background: ${({ theme }) => theme.redesign.errorColor};
  color: ${({ theme }) => theme.redesign.defaultBackground};
  height: 24px;
  z-index: 1;
  position: absolute;
  font-size: 14px;
  padding: 0 10px 0 6px;
  display: flex;
  align-items: center;
  border-radius: 20px;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
`;

const BoundContainer = styled.div<{ isCancelled?: boolean; variant: Variant }>`
  opacity: ${({ isCancelled }) => (isCancelled ? 0.25 : 1)};
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  margin: 0 20px;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.redesign.defaultColor};
  }

  @media ${Breakpoint.MinTablet} {
    margin: 0 30px;

    ${({ variant }) =>
      variant === Variant.Row
        ? `
    margin: 0;
    width: 50%;

    &:not(:last-child) {
      border-bottom: none;
    }
    `
        : `
    flex-direction: row;
    align-items: center;
    gap: 0;
    justify-content: flex-start;

    & > * {
      width: 50%;
    }
    `}
  }
`;

const AirportNames = styled(BoundHeavyText)``;

const FlightData = styled.div`
  display: flex;
`;

const FlightStop = styled.div<{ isDestination?: boolean }>`
  width: 77px;
  margin-left: ${({ isDestination }) => (isDestination ? "10px" : "0")};
  white-space: nowrap;
`;

export default FlightBounds;
